import { useEffect, useState } from "react";
import { useMyProviderDataClient } from "../../../../context/ApiContext";

// Shapes returned by GET /EmployeeInformation/QuarterlyMetrics (QuarterlyMetricsDTO).

export interface MetricValue {
  periodStart: string;
  value: number | null;          // null = N/A, nothing to measure
  meetsTarget: boolean | null;   // null = N/A
  numerator: number | null;
  denominator: number;
  itemCount: number | null;
  itemsMeetingTarget: number | null;
}

export interface QuarterlyMetric {
  key: string;
  name: string;
  sortOrder: number;
  unit: "Percent" | "Days";
  targetRule: "Value" | "AllItems";
  targetMin: number | null;
  targetMax: number | null;
  targetLabel: string;
  isComingSoon: boolean;
  description: string | null;
  explanation: string | null;   // legend text, one point per line
  monthValues: (MetricValue | null)[];   // one per month of the quarter, in order
  quarterValue: MetricValue | null;
}

export interface QuarterlyMetricClient {
  clientId: string;
  clientName: string | null;
  meetsAllTargets: boolean;
  metrics: Record<string, { monthValues: (MetricValue | null)[]; quarterValue: MetricValue | null }>;
}

export interface QuarterlyMetricsSummary {
  metricsTotal: number;
  metricsApplicable: number;
  metricsMet: number;
  allMetricsMet: boolean;
  score: number | null;              // met / applicable x 100, N/A metrics left out; null = none applicable
  requiredScore: number | null;      // pass mark (80)
  meetsRequirement: boolean | null;  // null when score is null
}

export interface QuarterlyMetrics {
  year: number;
  quarter: number;
  qStart: string;
  qEnd: string;
  calculatedAt: string | null;
  eprSyncedAt: string | null;      // when EPR_GenerateRows last updated the user's EPR row
  metricsBehindEpr: boolean;       // calculatedAt is older than eprSyncedAt
  months: string[];
  summary: QuarterlyMetricsSummary | null;
  metrics: QuarterlyMetric[];
  clients: QuarterlyMetricClient[];
}

export const useQuarterlyMetrics = (
  username?: string,
  year?: string,
  quarter?: number,
  enabled: boolean = true
) => {
  const client = useMyProviderDataClient();

  const [data, setData] = useState<QuarterlyMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!enabled || !username || !year || !quarter) return;

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(undefined);

        const res = await client.get("/EmployeeInformation/QuarterlyMetrics", {
          params: { username, year, quarter },
        });

        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error("Quarterly metrics load failed", err);
        if (!cancelled) {
          setData(null);
          setError("Unable to load quarterly performance.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    // a quarter picked while the previous one is still loading must not be overwritten by it
    return () => {
      cancelled = true;
    };
  }, [username, year, quarter, enabled, client]);

  return { data, loading, error };
};
